/**
 * ChatToolbar — Chat input toolbar component
 * 
 * Renders below the textarea inside the chat input container.
 * Fetches real model data from GET /chat/models.
 * All state persisted via chat-preferences localStorage store.
 * Feature toggles are hidden when their feature flag is disabled.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronDown, Globe, Brain, Sparkles, Zap,
} from 'lucide-react';
import { getApiUrl } from '../lib/env';
import { useAuth } from '../contexts/AuthContext';
import { type ChatPreferences } from '../config/chat-preferences';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const API_BASE = getApiUrl();

// ── Types ──────────────────────────────────────────────────────────────────────

interface ModelOption {
  id: string;
  provider: string;
  displayName: string;
  model: string;
  tier: string;
  description: string;
}

interface ChatToolbarProps {
  /** Current preferences (from parent hook) */
  preferences: ChatPreferences;
  /** Updater (from parent hook) */
  updatePreferences: (p: Partial<ChatPreferences>) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function ChatToolbar({ preferences, updatePreferences }: ChatToolbarProps) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  // Model dropdown
  const [models, setModels] = useState<ModelOption[]>([]);
  const [showModels, setShowModels] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch models once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await user?.getIdToken?.();
        const res = await fetch(`${API_BASE}/chat/models`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Failed to fetch models');
        const data = await res.json();
        if (!cancelled && Array.isArray(data.models)) {
          setModels(data.models);
        }
      } catch {
        // Fallback: show default model
        if (!cancelled) {
          setModels([{
            id: 'openai:quality',
            provider: 'openai',
            displayName: 'OpenAI',
            model: 'gpt-5.4-mini',
            tier: 'quality',
            description: 'Most capable model',
          }]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowModels(false);
      }
    };
    if (showModels) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showModels]);

  // Currently selected model info
  const selectedModel = models.find(m => m.id === preferences.modelId) || models[0];

  const selectModel = useCallback((id: string) => {
    updatePreferences({ modelId: id });
    setShowModels(false);
  }, [updatePreferences]);

  // ── Provider icon helper ─────────────────────────────────────────────────

  const ProviderIcon = ({ provider, tier }: { provider: string; tier: string }) => {
    if (tier === 'fast') return <Zap size={13} className="text-amber-400" />;
    if (provider === 'anthropic') return <Sparkles size={13} className="text-orange-400" />;
    return <Sparkles size={13} className="text-emerald-400" />;
  };

  // Check if any toggle is visible
  const showSearch = flags.web_search !== false;
  const showThinking = flags.extended_thinking !== false;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex items-center gap-1 pt-1 pb-0.5 px-1">
      {/* Left group */}
      <div className="flex items-center gap-1 flex-1 min-w-0">

        {/* Model selector pill */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setShowModels(prev => !prev)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium
                       text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover
                       transition-colors select-none"
            title={selectedModel ? `${selectedModel.displayName} (${selectedModel.model})` : 'Select model'}
          >
            {selectedModel && <ProviderIcon provider={selectedModel.provider} tier={selectedModel.tier} />}
            <span className="truncate max-w-[140px]">
              {selectedModel?.displayName || 'Model'}
            </span>
            <ChevronDown size={12} className={`transition-transform ${showModels ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown — opens UPWARD */}
          {showModels && models.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1.5 w-64 rounded-xl border border-chat-border
                            bg-chat-input shadow-xl shadow-black/20 backdrop-blur-md z-50 overflow-hidden">
              <div className="p-1.5">
                {models.map(m => {
                  const isActive = m.id === preferences.modelId;
                  return (
                    <button
                      key={m.id}
                      onClick={() => selectModel(m.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors
                        ${isActive
                          ? 'bg-hig-accent/10 text-hig-text-primary'
                          : 'text-hig-text-secondary hover:bg-chat-hover hover:text-hig-text-primary'
                        }`}
                    >
                      <ProviderIcon provider={m.provider} tier={m.tier} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{m.displayName}</div>
                        <div className="text-[11px] text-hig-text-tertiary truncate">{m.description}</div>
                      </div>
                      {isActive && (
                        <div className="w-1.5 h-1.5 rounded-full bg-hig-accent flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right group — toggles (only shown if feature flag is enabled) */}
      <div className="flex items-center gap-0.5">

        {/* Web Search toggle */}
        {showSearch && (
          <button
            onClick={() => updatePreferences({ webSearch: !preferences.webSearch })}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] font-medium transition-colors select-none
              ${preferences.webSearch
                ? 'bg-hig-accent/15 text-hig-accent'
                : 'text-hig-text-tertiary hover:text-hig-text-secondary hover:bg-chat-hover'
              }`}
            title={preferences.webSearch ? 'Web search enabled' : 'Enable web search'}
          >
            <Globe size={14} />
            <span className="hidden sm:inline">Search</span>
          </button>
        )}

        {/* Extended Thinking toggle */}
        {showThinking && (
          <button
            onClick={() => updatePreferences({ extendedThinking: !preferences.extendedThinking })}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] font-medium transition-colors select-none
              ${preferences.extendedThinking
                ? 'bg-hig-accent/15 text-hig-accent'
                : 'text-hig-text-tertiary hover:text-hig-text-secondary hover:bg-chat-hover'
              }`}
            title={preferences.extendedThinking ? 'Extended thinking enabled' : 'Enable extended thinking'}
          >
            <Brain size={14} />
            <span className="hidden sm:inline">Think</span>
          </button>
        )}
      </div>
    </div>
  );
}
